/** biome-ignore-all lint/suspicious/noBitwiseOperators: needed for color generation */
import Avatar from '@mui/material/Avatar';
import React from 'react';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';

const stringToColor = (string: string) => {
  let hash = 0;

  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

const getCountStyles = (count: number, isMobile: boolean) => {
  const isTwoDigits = count >= 10 && count < 100;
  const isThreeDigits = count > 99;
  const fontSize =
    isTwoDigits || isThreeDigits ? `${isMobile ? '12px' : '14px'} !important` : undefined;

  return {
    sx: {
      fontSize,
      color: '#141414',
      background: '#FFF',
      border: '1.5px solid #141414',
    },
    children: isThreeDigits ? '99+' : `+${count}`,
  };
};

/** First + last name initials, e.g. 'Ada Lovelace King' -> 'AK'. Empty if under two words. */
const getNameInitials = (displayName?: string) => {
  const words = displayName?.trim().split(/\s+/) ?? [];
  if (words.length < 2 || !words[0]) {
    return '';
  }
  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
};

const getLetterStyles = (string: string, capitalize: boolean, displayName?: string) => {
  const finalString = capitalize ? string.toUpperCase() : string;
  return {
    className: 'letter-avatar-letter',
    sx: { bgcolor: stringToColor(finalString) },
    children: getNameInitials(displayName) || finalString.charAt(0),
  };
};

const getSizing = (isMobile: boolean, isTripsList: boolean) => {
  if (!isMobile) {
    return {
      avatarSize: '40px',
      fontSize: '18px',
    };
  }

  if (isTripsList) {
    return {
      avatarSize: '28px',
      fontSize: '14px',
    };
  }

  return {
    avatarSize: '32px',
    fontSize: '16px',
  };
};

export interface LetterAvatarProps {
  string: string;
  capitalize?: boolean;
  remainingRolesCount?: number;
  displayName?: string;
}

function LetterAvatar({
  string,
  capitalize = false,
  remainingRolesCount = 0,
  displayName,
}: LetterAvatarProps) {
  const isMobile = useMobileMediaQuery();
  const isTripsList = window.location.pathname === '/trips';
  const { avatarSize, fontSize } = getSizing(isMobile, isTripsList);
  const avatarProps =
    remainingRolesCount > 0
      ? getCountStyles(remainingRolesCount, isMobile)
      : getLetterStyles(string, capitalize, displayName);

  return (
    <Avatar
      {...avatarProps}
      style={{ width: avatarSize, height: avatarSize, fontSize }}
    />
  );
}

LetterAvatar.displayName = 'LetterAvatar';
export default LetterAvatar;
